import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight, TrendingUp } from "lucide-react";
import api, { formatINR } from "../lib/api";
import { MaskedLines, Reveal } from "../components/Reveal";
import PropertyCard from "../components/PropertyCard";

function TrendChart({ points }) {
  const data = points.filter((p) => p.median != null);
  if (data.length < 2) return <p className="text-sm font-light text-smoke">Not enough history yet — check back as listings trade.</p>;
  const W = 660, H = 250, P = 42;
  const vals = data.map((p) => p.median);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min || 1;
  const x = (i) => P + (i * (W - 2 * P)) / (data.length - 1);
  const y = (v) => H - P - ((v - min) / span) * (H - 2 * P);
  const pts = data.map((p, i) => [x(i), y(p.median)]);
  const line = pts.map((pt, i) => `${i ? "L" : "M"}${pt[0].toFixed(1)},${pt[1].toFixed(1)}`).join(" ");
  const area = `${line} L${pts[pts.length - 1][0]},${H - P} L${pts[0][0]},${H - P} Z`;
  const first = data[0];
  const lastPt = data[data.length - 1];
  const change = ((lastPt.median - first.median) / first.median) * 100;
  return (
    <div>
      <div className="mb-8 flex flex-wrap items-baseline gap-4">
        <p className="font-serif text-3xl font-light text-bone lg:text-4xl" data-testid="trend-current-median">{formatINR(lastPt.median)}</p>
        <p className={`flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] ${change >= 0 ? "text-gold" : "text-red-400"}`} data-testid="trend-change">
          <TrendingUp size={13} /> {change >= 0 ? "+" : ""}{change.toFixed(1)}% · 6 mo
        </p>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" data-testid="trend-chart" role="img" aria-label="Median price trend">
        <defs>
          <linearGradient id="trendfill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#CBA153" stopOpacity="0.28" />
            <stop offset="100%" stopColor="#CBA153" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#trendfill)" />
        <path d={line} fill="none" stroke="#CBA153" strokeWidth="2" strokeLinejoin="round" />
        {pts.map((pt, i) => (
          <g key={data[i].month}>
            <circle cx={pt[0]} cy={pt[1]} r="4" fill="#050505" stroke="#CBA153" strokeWidth="2" data-testid={`trend-point-${data[i].month}`} />
            <text x={pt[0]} y={H - P + 20} textAnchor="middle" fill="#A3A3A3" fontSize="10" fontFamily="'Space Mono', monospace">{data[i].label}</text>
            <text x={pt[0]} y={pt[1] - 12} textAnchor="middle" fill="#F7F5F0" fontSize="10" fontFamily="'Space Mono', monospace">{formatINR(data[i].median)}</text>
          </g>
        ))}
      </svg>
    </div>
  );
}

export default function LocalityGuide() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);

  useEffect(() => {
    setData(null);
    setTrend(null);
    api.get(`/localities/${slug}`)
      .then(({ data }) => {
        setData(data);
        document.title = `${data.name} Guide — Gurgaon Estates`;
      })
      .catch(() => setData(false));
    api.get(`/localities/${slug}/trend`).then(({ data }) => setTrend(data)).catch(() => {});
  }, [slug]);

  if (data === null) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="animate-pulse-soft font-mono text-xs uppercase tracking-[0.3em] text-gold">Charting…</p>
      </div>
    );
  }
  if (data === false) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6" data-testid="locality-not-found">
        <p className="font-serif text-4xl text-bone">Uncharted ground.</p>
        <Link to="/localities" className="gold-link">← All locality guides</Link>
      </div>
    );
  }

  const stats = [
    { label: "Live Residences", value: data.listing_count },
    { label: "Median Ask", value: data.median_sale ? formatINR(data.median_sale) : "—" },
    { label: "Median Lease", value: data.median_rent ? formatINR(data.median_rent, "monthly") : "—" },
    { label: "Median ₹/ft²", value: data.median_psf ? `₹${data.median_psf.toLocaleString("en-IN")}` : "—" },
  ];

  return (
    <div data-testid="locality-guide-page">
      <section ref={heroRef} className="grain relative flex min-h-[85svh] items-end overflow-hidden" data-testid="locality-hero">
        <motion.div style={{ y: imgY }} className="absolute inset-0 scale-[1.12]">
          <img src={data.image} alt={data.name} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/25" />
          <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-ink/85 to-transparent" />
        </motion.div>
        <div className="relative z-10 mx-auto w-full max-w-[1600px] px-6 pb-20 pt-40 lg:px-12">
          <Link to="/localities" data-testid="back-to-localities" className="mb-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-smoke transition-colors hover:text-gold">
            <ArrowLeft size={14} /> The Atlas
          </Link>
          <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.4em] text-gold">Locality Guide · Gurugram</p>
          <MaskedLines
            lines={[data.name]}
            className="max-w-5xl font-serif text-[12vw] font-light leading-[0.95] tracking-tighter text-bone sm:text-7xl lg:text-8xl"
          />
          <Reveal delay={0.5}>
            <p className="mt-6 max-w-xl font-serif text-xl font-light italic text-bone/70">{data.short}</p>
          </Reveal>
        </div>
      </section>

      <section className="border-b border-white/10 bg-obsidian" data-testid="locality-stats">
        <div className="mx-auto grid max-w-[1600px] grid-cols-2 gap-px bg-white/10 lg:grid-cols-4">
          {stats.map((s) => (
            <div key={s.label} className="bg-obsidian px-6 py-10 lg:px-10">
              <p className="font-serif text-3xl font-light text-bone lg:text-4xl" data-testid={`stat-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{s.value}</p>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-gold">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {trend?.points?.some((p) => p.median != null) && (
        <section className="border-b border-white/10" data-testid="price-trend-section">
          <div className="mx-auto max-w-[1600px] px-6 py-20 lg:px-12">
            <Reveal>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Price Movement</p>
              <h2 className="mt-3 font-serif text-4xl font-light text-bone sm:text-5xl">Median ask, six months</h2>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="mt-10 border border-white/10 bg-obsidian p-6 lg:p-10">
                <TrendChart points={trend.points} />
              </div>
              <p className="mt-4 font-mono text-[9px] uppercase tracking-[0.2em] text-smoke" data-testid="trend-disclaimer">
                Indicative curve — live points accrue as listings publish, sell and relist
              </p>
            </Reveal>
          </div>
        </section>
      )}

      {data.paragraphs?.length > 0 && (
        <section className="mx-auto max-w-[1600px] px-6 py-24 lg:px-12" data-testid="locality-editorial">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <Reveal className="lg:col-span-3">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">The Character</p>
            </Reveal>
            <div className="space-y-8 lg:col-span-7">
              {data.paragraphs.map((p, i) => (
                <Reveal key={i} delay={i * 0.1}>
                  <p className="font-serif text-xl font-light leading-relaxed text-bone/85 sm:text-2xl">{p}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="mx-auto max-w-[1600px] px-6 pb-32 lg:px-12" data-testid="locality-listings">
        <Reveal>
          <div className="mb-12 flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">On the Market</p>
              <h2 className="mt-3 font-serif text-4xl font-light text-bone sm:text-5xl">Live in {data.name}</h2>
            </div>
            <Link to={`/listings?locality=${encodeURIComponent(data.name)}`} data-testid="locality-filtered-link" className="gold-link">
              Open in filters →
            </Link>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 xl:grid-cols-3">
          {data.properties.map((p, i) => (
            <Reveal key={p.id} delay={Math.min(i, 5) * 0.06}>
              <PropertyCard property={p} index={i} />
            </Reveal>
          ))}
        </div>
        {data.properties.length === 0 && (
          <div className="border border-white/10 p-20 text-center">
            <p className="font-serif text-3xl text-bone">Quiet at the moment.</p>
            <p className="mt-3 text-sm font-light text-smoke">Register as a buyer and we'll call you the day something opens up here.</p>
            <Link to="/register" data-testid="locality-register-cta" className="mt-8 inline-flex items-center gap-3 bg-gold px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] text-ink transition-colors hover:bg-bone">
              Get notified <ArrowRight size={15} />
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}
